import { MouseEvent, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  Dialog,
  DialogContent,
  Popper,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { HangUpButton, Transitions } from 'components';
import { useVideo, useChat, useIsGuestCall, useRoomHost } from 'hooks';
import { useApiRequests } from 'hooks/useApiRequests/useApiRequests';

import styles from './EndCall.module.scss';

type EndCallProps = {
  disabled?: boolean;
  withTooltip?: boolean;
  onEndCall?: () => void;
};

export function EndCall(props: EndCallProps) {
  const { t } = useTranslation('providerRoom');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isGuestCall = useIsGuestCall();
  const { closeRoomChat } = useChat();
  const { room } = useVideo();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const prevOpen = useRef(open);
  const { isHost } = useRoomHost();
  const ApiRequests = useApiRequests();

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current && anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleLeaveCall = async () => {
    if (isHost && room?.name) {
      try {
        await ApiRequests.endCall(room.name, new Date().toISOString());
      } catch (e) {
        console.warn('Failed to end call via API before disconnect:', e);
      }
    }

    room!.disconnect();
    closeRoomChat();
    props?.onEndCall && props?.onEndCall();
  };

  return (
    <>
      <HangUpButton
        withTooltip={!open && props.withTooltip}
        onClick={handleToggle}
        ref={anchorRef}
        disabled={props.disabled}
      />

      <Popper
        placement='top'
        open={open && !isMobile}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              enabled: true,
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose}>
              <Box className={styles.menu} sx={{ backgroundColor: isGuestCall ? '#FFFFFF' : 'rgba(0, 0, 0, 0.5);' }}>
                <Stack direction='column' spacing={'10px'} alignItems='flex-start'>
                  <Typography
                    component='p'
                    variant='body2'
                    color={isGuestCall ? '#393534' : '#FFF'}
                    className={styles.confirmationTitle}
                  >
                    {t('areYouSure')}
                  </Typography>
                  <Button
                    variant='contained'
                    fullWidth
                    sx={{ backgroundColor: '#FF3D3D !important' }}
                    onClick={handleLeaveCall}
                  >
                    {isHost ? t('endCall') : t('leaveCall')}
                  </Button>
                  <Button
                    variant='outlined'
                    fullWidth
                    className={`endcall-cancel-btn ${isGuestCall ? 'endcall-cancel-btn-guest' : ''} ${
                      styles.cancelButton
                    } ${isGuestCall ? styles.cancelButtonGuest : ''}`.trim()}
                    onClick={handleClose}
                  >
                    {t('cancel')}
                  </Button>
                </Stack>
              </Box>
            </ClickAwayListener>
          </Transitions>
        )}
      </Popper>

      <Dialog open={open && isMobile} className={styles.dialog}>
        <DialogContent>
          <Typography variant='body2' sx={{ color: 'var(--app-text-default)' }} className={styles.confirmationTitle}>
            {t('areYouSure')}
          </Typography>

          <Button
            variant='contained'
            fullWidth
            className={styles.formButton}
            sx={{ backgroundColor: '#FF3D3D !important', color: '#FFF !important' }}
            onClick={handleLeaveCall}
          >
            {isHost ? t('endCall') : t('leaveCall')}
          </Button>

          <Button
            variant='outlined'
            fullWidth
            className={`${styles.formButton} ${styles.cancelButtonDialog}`.trim()}
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
